// ============================================================================
// Frontend ↔ Backend enum mappings
// Frontend uses English keys, backend expects German literals
// ============================================================================
export const SOIL_TYPE_TO_BACKEND = {
  sandy: 'Sand', loamy: 'Lehm', clay: 'Ton', loess: 'Löss',
  mixed: 'Mischboden', humus: 'Mischboden',
  // Already-correct backend values (passthrough for round-trip)
  Sand: 'Sand', Lehm: 'Lehm', Ton: 'Ton', 'Löss': 'Löss', Mischboden: 'Mischboden',
};

const SOIL_TYPE_TO_FRONTEND = {
  Sand: 'sandy', Lehm: 'loamy', Ton: 'clay', 'Löss': 'loess', Mischboden: 'mixed',
};

export const DRAINAGE_TO_BACKEND = {
  good: 'Gut', medium: 'Mittel', poor: 'Schlecht',
  Gut: 'Gut', Mittel: 'Mittel', Schlecht: 'Schlecht',
};

const DRAINAGE_TO_FRONTEND = {
  Gut: 'good', Mittel: 'medium', Schlecht: 'poor',
};

export const ORIENTATION_TO_BACKEND = {
  north: 'N', northeast: 'NO', east: 'O', southeast: 'SO',
  south: 'S', southwest: 'SW', west: 'W', northwest: 'NW',
  N: 'N', NO: 'NO', O: 'O', SO: 'SO', S: 'S', SW: 'SW', W: 'W', NW: 'NW',
};

const ORIENTATION_TO_FRONTEND = {
  N: 'north', NO: 'northeast', O: 'east', SO: 'southeast',
  S: 'south', SW: 'southwest', W: 'west', NW: 'northwest',
};

/**
 * Map backend snake_case response → frontend camelCase settings
 * Pure function for easy testing.
 */
export function mapBackendToFrontend(backendGarden) {
  const s = backendGarden.settings || {};
  return {
    gardenId: String(backendGarden.id),
    name: backendGarden.name,
    soilType: SOIL_TYPE_TO_FRONTEND[s.soilType] || s.soilType,
    phValue: s.phValue,
    drainage: DRAINAGE_TO_FRONTEND[s.drainage] || s.drainage,
    orientation: ORIENTATION_TO_FRONTEND[s.orientation] || s.orientation,
    // null = unset (compass shows "?"), preserved across the round-trip (#465)
    northAngle: s.northAngle ?? null,
    // Layer view-state (#466). null = legacy garden → LayerContext falls back
    // to DEFAULT_* state. Preserved across the round-trip.
    layers: s.layers ?? null,
    zipCode: s.zipCode,
    city: s.city,
    ...(s.country && { country: s.country }),
    ...(s.hardinessZone && { hardinessZone: s.hardinessZone }),
    ...(s.sunlight && { sunlight: s.sunlight }),
    // Geo-Koordinaten für den Sonnenstand (#604). Nur wenn vorhanden — Altgärten
    // ohne lat/lon heilen sich zur Laufzeit über den PLZ-Fallback (coords.js).
    ...(Number.isFinite(s.latitude) && { latitude: s.latitude }),
    ...(Number.isFinite(s.longitude) && { longitude: s.longitude }),
    backgroundImage: backendGarden.background_image || null,
    outline: backendGarden.outline || null,
    heightDifferences: backendGarden.height_zones || null,
    favorites: backendGarden.favorites || [],
  };
}

/**
 * Map frontend camelCase → backend payload for POST/PATCH
 * Pure function for easy testing.
 */
export function mapFrontendToBackend(settings, canvasObjects, favorites) {
  const payload = {
    name: settings.name || 'Mein Garten',
    settings: {
      soilType: SOIL_TYPE_TO_BACKEND[settings.soilType] || settings.soilType,
      phValue: settings.phValue,
      drainage: DRAINAGE_TO_BACKEND[settings.drainage] || settings.drainage,
      orientation: ORIENTATION_TO_BACKEND[settings.orientation] || settings.orientation,
      // 0 is a valid angle (north) — send explicitly, fall back to null when unset (#465)
      northAngle: settings.northAngle ?? null,
      zipCode: settings.zipCode,
      city: settings.city,
      ...(settings.country && { country: settings.country }),
      ...(settings.hardinessZone && { hardinessZone: settings.hardinessZone }),
      ...(settings.sunlight && { sunlight: settings.sunlight }),
      // Geo-Koordinaten für den Sonnenstand (#604). Nur senden wenn gesetzt,
      // damit Altgärten kein leeres Feld schreiben (Muster wie northAngle/layers).
      ...(Number.isFinite(settings.latitude) && { latitude: settings.latitude }),
      ...(Number.isFinite(settings.longitude) && { longitude: settings.longitude }),
      // Layer view-state (#466). Only sent when present, so legacy gardens that
      // never touched layers don't write an empty array (matches northAngle's
      // optional-editor-field pattern).
      ...(settings.layers && { layers: settings.layers }),
    },
    // outline is required in GardenCreate — always include it
    outline: settings.outline,
  };

  const bgImage = sanitizeBackgroundImage(settings.backgroundImage);
  if (bgImage) {
    payload.backgroundImage = bgImage;
  } else if (!settings.backgroundImage) {
    // No background in settings → send an explicit null so a removal (#766)
    // actually clears the stored image. The backend PATCH uses
    // exclude_unset=True, so omitting the field (the previous behavior) left
    // the old image in place and it returned on the next reload. Harmless for
    // gardens that never had a background. Note: an uncalibrated draft object
    // (truthy, but sanitizes to undefined) still falls through to omission.
    payload.backgroundImage = null;
  }
  if (settings.heightDifferences) {
    payload.heightDifferences = settings.heightDifferences;
  }
  if (canvasObjects) {
    payload.canvasObjects = canvasObjects;
  }
  payload.favorites = favorites || [];

  return payload;
}

/**
 * Sanitize backgroundImage for backend.
 *
 * - Strips frontend-only fields (file, dataUrl, canvasSize).
 * - Keeps calibration only when the user actually completed it
 *   (`isCalibrated === true`). An uncalibrated draft is dropped so the editor
 *   falls back to its default 100 px/m on reload instead of inheriting a
 *   fake scale (#453).
 * - For a user-completed calibration, repairs invalid numeric fields so
 *   the backend's `gt=0` validators don't reject the payload.
 * - Returns `undefined` when nothing meaningful remains (no imageUrl AND
 *   no calibration) — matching the backend rule that an empty
 *   backgroundImage object is rejected.
 */
export function sanitizeBackgroundImage(bgImage) {
  if (!bgImage) return undefined;

  // eslint-disable-next-line no-unused-vars -- explicit strip via destructure: drop file/dataUrl/canvasSize
  const { file, dataUrl, canvasSize, ...sanitized } = bgImage;

  if (sanitized.calibration?.isCalibrated === true) {
    sanitized.calibration = { ...sanitized.calibration };
    const cal = sanitized.calibration;

    if (!cal.realDistance || cal.realDistance <= 0) {
      cal.realDistance = 5;
    }
    if (!cal.pixelDistance || cal.pixelDistance <= 0) {
      cal.pixelDistance = (cal.scale && cal.scale > 0)
        ? cal.realDistance * cal.scale
        : cal.realDistance * 100;
    }
    if (!cal.scale || cal.scale <= 0) {
      cal.scale = cal.pixelDistance / cal.realDistance;
    }
  } else {
    delete sanitized.calibration;
  }

  if (!sanitized.imageUrl && !sanitized.calibration) {
    return undefined;
  }

  return sanitized;
}
